import { use, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import config from '@app/config'
import { ChatContext } from '@app/context'
import { DEFAULT_LANG } from '@app/store/translation/constants'
import { DEFAULT_LANGUAGE } from '@app/utils/regional'

import { useB2BAgentWarningMessage } from './B2B/useAgentWarningMessage'
import { buildNavigationModel } from './buildNavigationModel'
import {
  NAVIGATION_MODEL_VARIANT,
  type NavigationData,
  type NavigationModelVariant,
} from './navigationModel'
import type {
  NavigationItem,
  NavigationSubmenuItem,
  UserWithAvatar,
} from './types'
import { useNavigationVisibleContext } from './useNavigationVisibleContext'
import { useUser } from '../user/useUser'

import type { TFunction } from 'i18next'

const TRANSLATION_KEY_PATTERN = /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)+$/

const getString = (value: unknown): string | null => {
  return typeof value === 'string' && value.length > 0 ? value : null
}

const translateKey = (
  key: string,
  t: TFunction,
  lng?: string
): string | null => {
  return getString(
    t(key, {
      lng,
      returnObjects: true,
      defaultValue: '',
    }) as unknown
  )
}

const translateIfKey = (value: string | undefined, t: TFunction): string => {
  if (!value) {
    return ''
  }

  if (!TRANSLATION_KEY_PATTERN.test(value)) {
    return value
  }

  const translatedString = translateKey(value, t)

  if (translatedString) {
    return translatedString
  }

  const labelFallbackKey = `${value}.Label`
  const labelFallbackString = translateKey(labelFallbackKey, t)

  if (labelFallbackString) {
    return labelFallbackString
  }

  const fallbackLanguages = [DEFAULT_LANG, DEFAULT_LANGUAGE]

  for (const fallbackLanguage of fallbackLanguages) {
    const fallbackTranslation = translateKey(value, t, fallbackLanguage)

    if (fallbackTranslation) {
      return fallbackTranslation
    }

    const fallbackLabelTranslation = translateKey(
      labelFallbackKey,
      t,
      fallbackLanguage
    )

    if (fallbackLabelTranslation) {
      return fallbackLabelTranslation
    }
  }

  return value
}

const translateNavigationData = (
  data: NavigationData,
  t: TFunction
): NavigationData => {
  return {
    ...data,
    profile: data.profile
      ? {
        ...data.profile,
        label: translateIfKey(data.profile.label, t),
        username: translateIfKey(data.profile.username, t),
        actingAsLabel: translateIfKey(data.profile.actingAsLabel, t),
        agentWarningMessage: translateIfKey(
          data.profile.agentWarningMessage,
          t
        ),
      }
      : data.profile,
    items: (data.items as NavigationItem[]).map((item) => ({
      ...item,
      label: translateIfKey(item.label, t),
      desktop: item.desktop
        ? {
          ...item.desktop,
          label: translateIfKey(item.desktop.label, t),
        }
        : item.desktop,
      submenu: item.submenu?.map((submenuItem) => {
        const typedSubmenuItem = submenuItem as NavigationSubmenuItem

        return {
          ...typedSubmenuItem,
          label: translateIfKey(typedSubmenuItem.label, t),
          description: translateIfKey(typedSubmenuItem.description, t),
        }
      }),
    })),
  }
}

export const useNavigationModel = (
  variant: NavigationModelVariant = NAVIGATION_MODEL_VARIANT.B2B
) => {
  const { t } = useTranslation()
  const { state: chatState } = use(ChatContext)
  const userState = useUser()
  const currentUser = userState.currentUser as UserWithAvatar
  const visibleContext = useNavigationVisibleContext(userState, variant)
  const b2bProfileAgentWarningMessage = useB2BAgentWarningMessage({
    user: currentUser,
    isStaff: visibleContext.isStaff,
  })
  const profileAgentWarningMessage =
    variant === NAVIGATION_MODEL_VARIANT.B2B && visibleContext.isAgent
      ? b2bProfileAgentWarningMessage
      : ''

  return useMemo(() => {
    const model = buildNavigationModel({
      variant,
      userState,
      profileAgentWarningMessage,
      visibleContext,
      isWebView: Boolean(config.identity.isWebView),
      unreadTotal: chatState.unreadTotal,
    })

    return translateNavigationData(model, t)
  }, [
    chatState.unreadTotal,
    profileAgentWarningMessage,
    t,
    userState,
    variant,
    visibleContext,
  ])
}
